import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    Banknote,
    BarChart3,
    CheckCircle2,
    Clock,
    CreditCard,
    DollarSign,
    LayoutDashboard,
    Package,
    QrCode,
    Receipt,
    RefreshCw,
    RotateCcw,
    ShoppingCart,
    Tag,
    TrendingUp,
    Wallet,
    XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import AnimatedNumber, { AnimatedMoney } from '../../component/common/AnimatedNumber';
import './dashboard.css';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import KpiTickerCard, { KpiDeltaChip } from '../../component/report/KpiTickerCard';
import { KitCard, SecH } from '../../component/common/MobileKit';
import {
    BarsChart,
    DonutCard,
    HBars,
    Kpi,
    KpiGrid,
    KitEmptyNote,
    PERIOD_OPTIONS,
    PeriodChips,
    detectPeriod,
    formatMoneyShort,
    parseMoney,
    periodRange,
} from '../../component/common/MobileDashboardKit';

// Card header (`.card-head`): 36px icon chip (tinted bg/fg) + title + one
// line subtitle — DESIGN-SYSTEM.md §5.2.
function CardHeadRow({ icon, iconBg, iconFg, title, subtitle, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={140} /> : subtitle}
                </Typography>
            </Box>
        </Box>
    );
}

// `.summary-row` — label (icon chip + text) left, bold value right.
function SummaryRow({ icon, iconBg, iconFg, label, value, valueColor, chip, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '8px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: iconBg,
                    color: iconFg,
                }}>
                    {icon}
                </Box>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {label}
                </Typography>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={20} />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {chip}
                    <Typography sx={{ fontSize: '15px', fontWeight: 800, color: valueColor || 'text.primary' }}>
                        {value}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// `.pay-row` — 32px icon chip + label with proportional track + bold value and
// "% do bruto" (prototipo-dashboard-vendas.html).
function PaymentRow({ icon, iconBg, iconFg, label, value, barWidth, pctLabel, barColor, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '11px', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
            <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>
                    {label}
                </Typography>
                <Box sx={{ height: 5, borderRadius: '4px', bgcolor: 'background.subtle', overflow: 'hidden' }}>
                    {!loading && <Box sx={{ height: '100%', borderRadius: '4px', width: `${barWidth}%`, bgcolor: barColor, transition: 'width .4s ease' }} />}
                </Box>
            </Box>
            {loading ? (
                <Skeleton variant="text" width={70} height={18} />
            ) : (
                <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary' }}>
                        {value}
                    </Typography>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>
                        {pctLabel}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

// Rótulo compacto acima de cada barra do gráfico "Vendas por Dia"
// (`.bar-col .val` no protótipo, ex. "R$ 8,2k") — abrevia valores >= 1000.
function formatCompactCurrency(value) {
    if (value === null || value === undefined) return '';
    const abs = Math.abs(value);
    if (abs >= 1000) {
        return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// DESIGN-SYSTEM.md §5.11 empty state — centered dashed-circle icon + bold
// line + smaller instructional line. Used for the status card and the bar
// chart when the selected period has no data.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// Razão em pontos-base (3470 = 34,7%) e percentual simples, pt-BR com 1 casa.
const fmtBp = (bp) => `${((bp || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const fmtPct = (num, den) => (den > 0 ? `${((num / den) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '—');

export default function SaleDashboard() {
    const isMobileShell = useIsMobileShell();
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    // `POST /chart/sales/kpi-trend` exige `report.sale` (fv-api/config/router.go); sem ela
    // nem chamamos (um 403 abriria o modal global) — os cards mostram só os totais do dashboard.
    const canKpiTrend = hasPermission('report.sale');
    const [kpiTrend, setKpiTrend] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [totalSaleByDay, setTotalSaleByDay] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resumeValues, setResumeValues] = useState({
        total_amount_sale_gross: '0,00',
        total_taxes: '0,00',
        total_discount: '0,00',
        total_amount_net: '0,00',
        total_amount_pix: '0,00',
        total_amount_credit_card: '0,00',
        total_amount_debit_card: '0,00',
        total_amount_cash: '0,00',
        chart_sale_status: [],
        chart_total_sale_by_day: [],
        count_sales: 0
    });
    const [messageDialog, setMessageDialog] = useState();
    const [openMessageDialog, setOpenMessageDialog] = useState(false);

    const fetchResumeValues = async () => {
        const response = await httpClient.get(`/dashboard/sale/resume-values?startDate=${startDate.format('DD/MM/YYYY')}&endDate=${endDate.format('DD/MM/YYYY')}`);
        return response;
    }

    const fetchLastSales = async () => {
        const response = await httpClient.get(`/sales?startDate=${startDate.format('DD/MM/YYYY')}&endDate=${endDate.format('DD/MM/YYYY')}`);
        return response;
    }

    // Único lookup de cor por status de venda desta tela — mesmos 3 status
    // que `GetReportBySaleStatus` (fv-api/dashboard/sale_repository.go) pode
    // retornar (Finalizada/Cancelada/Estornada), com fallback neutro para
    // qualquer valor inesperado. Cores vêm do tema (DESIGN-SYSTEM.md §4),
    // nunca de hex fixo.
    const getStatusTone = useCallback((status) => {
        const key = (status || '').toLowerCase();
        if (key === 'finalizada') return { fg: theme.palette.success.main, bg: theme.palette.success.bg };
        if (key === 'cancelada') return { fg: theme.palette.error.main, bg: theme.palette.error.bg };
        if (key === 'estornada') return { fg: theme.palette.warning.main, bg: theme.palette.warning.bg };
        if (key === 'pendente') return { fg: theme.palette.warning.main, bg: theme.palette.warning.bg };
        if (key === 'em processamento') return { fg: theme.palette.secondary.main, bg: theme.palette.secondary.bg };
        return { fg: theme.palette.text.disabled, bg: theme.palette.background.subtle };
    }, [theme]);

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            // Aguarda ambas as chamadas terminarem simultaneamente, para que o
            // loading só termine depois que todos os dados estiverem prontos.
            // A segunda chamada (`/sales`) é mantida para preservar o mesmo
            // comportamento de rede da versão anterior desta tela — hoje não
            // alimenta nenhum elemento visual (a tabela de "últimas vendas"
            // já estava desativada antes deste redesign).
            const [resumeResponse] = await Promise.all([
                fetchResumeValues(),
                fetchLastSales()
            ]);

            if (resumeResponse.data.chart_sale_status.length === 0) {
                setMessageDialog('Nenhum registro encontrado no período selecionado');
                setOpenMessageDialog(true);
            } else {
                setResumeValues(resumeResponse.data);
            }
            setTotalSaleByDay(resumeResponse.data.chart_total_sale_by_day || []);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Variação vs. período anterior + sparklines. Falha (ou falta de permissão)
    // não derruba o dashboard: apenas some a variação.
    const loadKpiTrend = useCallback(async () => {
        if (!canKpiTrend || !startDate?.isValid?.() || !endDate?.isValid?.()) {
            setKpiTrend(null);
            return;
        }
        try {
            const res = await httpClient.post('/chart/sales/kpi-trend', {
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            });
            setKpiTrend(res.status >= 200 && res.status < 300 ? res.data : null);
        } catch (error) {
            setKpiTrend(null);
        }
    }, [canKpiTrend, startDate, endDate]);

    const handleRefresh = async () => {
        await Promise.all([loadAllData(), loadKpiTrend()]);
    };

    useEffect(() => {
        loadKpiTrend();
    }, [loadKpiTrend]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // No mobile, o refresh vai para o AppBar (e sai da tela).
    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: handleRefresh,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });

    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    const pieData = (resumeValues.chart_sale_status || []).map((item, index) => ({
        id: index,
        value: item.value,
        label: item.label,
        color: getStatusTone(item.label).fg,
    }));
    const hasStatusData = pieData.some((item) => item.value > 0);
    const statusCount = (name) => (resumeValues.chart_sale_status || []).find((item) => (item.label || '').toLowerCase() === name)?.value || 0;
    const finalizedCount = statusCount('finalizada');
    const canceledCount = statusCount('cancelada');
    const grossValue = parseMoney(resumeValues.total_amount_sale_gross);
    const netValue = parseMoney(resumeValues.total_amount_net);
    const taxesValue = parseMoney(resumeValues.total_taxes);
    const discountValue = parseMoney(resumeValues.total_discount);
    // Composição do Total Bruto (barra do hero): líquido + taxas. Descontos já estão
    // abatidos nos pagamentos (o bruto é a soma dos pagamentos), por isso não entram na barra.
    const netShare = grossValue > 0 ? Math.min(100, Math.max(0, (netValue / grossValue) * 100)) : 0;
    const taxShare = grossValue > 0 ? Math.min(100 - netShare, Math.max(0, (taxesValue / grossValue) * 100)) : 0;
    // Trilha proporcional ao maior método (100% = maior), como no protótipo.
    const payMax = Math.max(...['credit_card', 'debit_card', 'pix', 'cash'].map((k) => parseMoney(resumeValues[`total_amount_${k}`])), 0);
    const payBar = (raw) => (payMax > 0 ? (parseMoney(raw) / payMax) * 100 : 0);
    // Status: trilha proporcional ao maior status; % sobre o total de vendas listadas.
    const statusList = resumeValues.chart_sale_status || [];
    const statusMax = Math.max(...statusList.map((item) => item.value || 0), 0);
    const statusTotal = statusList.reduce((acc, item) => acc + (item.value || 0), 0);
    const statusBar = (v) => (statusMax > 0 ? ((v || 0) / statusMax) * 100 : 0);
    const getStatusIcon = (status) => {
        const key = (status || '').toLowerCase();
        if (key === 'finalizada') return <CheckCircle2 size={15} strokeWidth={2} />;
        if (key === 'cancelada') return <XCircle size={15} strokeWidth={2} />;
        if (key === 'estornada') return <RotateCcw size={15} strokeWidth={2} />;
        if (key === 'pendente' || key === 'em processamento') return <Clock size={15} strokeWidth={2} />;
        return <Tag size={15} strokeWidth={2} />;
    };
    const hasBarData =(totalSaleByDay || []).some((item) => item.total > 0);

    if (isMobileShell) {
        const gross = parseMoney(resumeValues.total_amount_sale_gross);
        const net = parseMoney(resumeValues.total_amount_net);
        const count = resumeValues.count_sales || 0;
        const margin = gross > 0 ? Math.round((net / gross) * 100) : 0;
        const days = (totalSaleByDay || []).map((d) => {
            const [dd, mm] = String(d.day || '').split('/');
            const date = dayjs(`${dayjs().year()}-${mm}-${dd}`);
            const label = (totalSaleByDay || []).length <= 7 && date.isValid() ? date.locale('pt-br').format('dd').charAt(0).toUpperCase() : dd;
            return { label, value: d.total };
        });
        const methods = [
            { label: 'Pix', value: parseMoney(resumeValues.total_amount_pix), color: theme.palette.primary.main },
            { label: 'Crédito', value: parseMoney(resumeValues.total_amount_credit_card), color: theme.palette.brand2.main },
            { label: 'Débito', value: parseMoney(resumeValues.total_amount_debit_card), color: theme.palette.secondary.main },
            { label: 'Dinheiro', value: parseMoney(resumeValues.total_amount_cash), color: theme.palette.warning.main },
        ];
        const methodsTotal = methods.reduce((sum, m) => sum + m.value, 0);
        const statusItems = (resumeValues.chart_sale_status || []).map((item) => ({ name: item.label, value: item.value, text: item.value.toLocaleString('pt-BR') }));
        return (
            <PageScaffold title="Dashboard de Vendas" titlePageTarget="Vendas" links={[{ title: "Dashboard", link: "" }]} allowBleed>
                <PeriodChips
                    options={PERIOD_OPTIONS}
                    activeId={detectPeriod(startDate, endDate)}
                    onChange={(id) => { const r = periodRange(id); setStartDate(r.start); setEndDate(r.end); }}
                />
                <KpiGrid>
                    <Kpi grad label="Faturamento" icon="dollar" value={formatMoneyShort(gross)} loading={loading} />
                    <Kpi label="Vendas" icon="receipt" value={count.toLocaleString('pt-BR')} loading={loading} />
                    <Kpi label="Ticket médio" icon="tag" value={formatMoneyShort(count > 0 ? gross / count : 0)} loading={loading} />
                    <Kpi label="Líquido" icon="trendup" value={formatMoneyShort(net)} delta={{ text: `${margin}% margem`, tone: 'up' }} loading={loading} />
                </KpiGrid>
                <SecH title="Faturamento por dia" />
                <KitCard>
                    {hasBarData ? <BarsChart items={days} /> : <KitEmptyNote>Nenhuma venda no período</KitEmptyNote>}
                </KitCard>
                <SecH title="Formas de pagamento" />
                {methodsTotal > 0 ? (
                    <DonutCard
                        segments={methods}
                        center={count}
                        legend={methods.map((m) => ({ label: `${m.label} ${Math.round((m.value / methodsTotal) * 100)}%`, color: m.color }))}
                    />
                ) : (
                    <KitCard><KitEmptyNote>Nenhum pagamento no período</KitEmptyNote></KitCard>
                )}
                <SecH title="Status das vendas" />
                <KitCard>
                    {hasStatusData ? <HBars items={statusItems} /> : <KitEmptyNote>Nenhum dado disponível</KitEmptyNote>}
                </KitCard>
                <DialogForm
                    open={openMessageDialog}
                    onClose={() => setOpenMessageDialog(false)}
                    onSubmit={() => setOpenMessageDialog(false)}
                    title={'Atenção'}
                    iconType="warning"
                    content={messageDialog}
                    cancelButtonText="Fechar"
                    hideConfirmButton
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold title="Dashboard de Vendas" titlePageTarget="Vendas" links={[{ title: "Dashboard", link: "" }]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição (DESIGN-SYSTEM.md §3.3/§6.1). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                    }}>
                        <LayoutDashboard size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Dashboard de Vendas
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Resumo financeiro, indicadores com variação, formas de pagamento e evolução das vendas no período.
                        </Typography>
                    </Box>
                    </Box>

                    {/* Período + atualizar, alinhados à direita na linha do page-head. */}
                    <DesktopPeriodSelector
                        start={startDate}
                        end={endDate}
                        onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onRefresh={handleRefresh}
                        loading={loading}
                        dualMonth
                    />
                </Box>

                {/* Indicadores do período — cards ticker (prototipo-dashboard-vendas.html).
                    Hero "Faturamento líquido" (6 col x 3 linhas) absorve o antigo Resumo
                    Financeiro; os demais seguem a ordem do protótipo. Descontos e canceladas
                    são "ruins quando sobem" (`inverse`), igual ao relatório de vendas. */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                    <Typography component="h3" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores do período</Typography>
                    {kpiTrend?.days?.length > 0 && (
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                            {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia' : 'dias'} · vs. {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia anterior' : 'dias anteriores'}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    gap: '10px',
                    mb: 2,
                    '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
                    '& > :first-of-type': { gridColumn: { xs: 'span 12', lg: 'span 6' }, gridRow: { lg: 'span 3' } },
                    '& > :last-child': { gridColumn: { xs: 'span 12', sm: 'span 6' } },
                }}>
                    <KpiTickerCard
                        hero large cumulative uid="dsf" kind="money"
                        label="Resumo financeiro · Faturamento líquido"
                        icon={<Wallet size={13} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main"
                        data={kpiTrend?.revenue} days={kpiTrend?.days}
                        value={Math.round(netValue * 100)} loading={loading}
                        footer={
                            <>
                                <Box title="Composição do Total Bruto" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '6px' }}>
                                    <Box sx={{ width: `${netShare}%`, bgcolor: 'success.main' }} />
                                    <Box sx={{ width: `${taxShare}%`, bgcolor: 'secondary.main' }} />
                                </Box>
                                <SummaryRow
                                    icon={<DollarSign size={13} strokeWidth={2} />} iconBg="info.bg" iconFg="info.main"
                                    label="Total Bruto"
                                    value={<AnimatedMoney value={resumeValues.total_amount_sale_gross} />}
                                />
                                <SummaryRow
                                    icon={<Tag size={13} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main"
                                    label={<>Taxas <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct(taxesValue, grossValue)} do bruto</Box></>}
                                    value={<AnimatedMoney value={resumeValues.total_taxes} prefix="− R$ " />}
                                />
                                <SummaryRow
                                    icon={<Tag size={13} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main"
                                    label={<>Descontos concedidos <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct(discountValue, grossValue)} do bruto</Box></>}
                                    chip={<KpiDeltaChip kind="money" inverse data={kpiTrend?.discounts} />}
                                    value={<AnimatedMoney value={resumeValues.total_discount} prefix="− R$ " />}
                                    valueColor="error.main"
                                />
                                <Divider sx={{ mt: 0.5 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.75 }}>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Total Líquido</Typography>
                                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'success.main' }}>
                                        <AnimatedMoney value={resumeValues.total_amount_net} />
                                    </Typography>
                                </Box>
                            </>
                        }
                    />
                    <KpiTickerCard cumulative uid="dqt" kind="units" label="Vendas realizadas" icon={<ShoppingCart size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={kpiTrend?.sales} days={kpiTrend?.days} value={finalizedCount} loading={loading}
                        note={kpiTrend && 'Finalizadas · exclui pendentes, estornadas e canceladas'} />
                    <KpiTickerCard uid="dtk" kind="money" label="Ticket médio" icon={<Receipt size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" data={kpiTrend?.ticket} days={kpiTrend?.days} value={finalizedCount > 0 ? Math.round((netValue * 100) / finalizedCount) : 0} loading={loading} />
                    <KpiTickerCard cumulative uid="dit" kind="units" label="Itens vendidos" icon={<Package size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.items} days={kpiTrend?.days} loading={loading} />
                    <KpiTickerCard cumulative uid="dlu" kind="money" label="Lucro bruto" icon={<TrendingUp size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" data={kpiTrend?.profit} days={kpiTrend?.days} loading={loading}
                        note={kpiTrend && <>Margem <b>{fmtBp(kpiTrend.margin.total)}</b> · ant. {fmtBp(kpiTrend.margin.previous_total)}</>} />
                    <KpiTickerCard cumulative inverse uid="dca" kind="units" label="Vendas canceladas" icon={<XCircle size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" data={kpiTrend?.canceled} days={kpiTrend?.days} value={canceledCount} loading={loading}
                        note={kpiTrend && <><b>{fmtBp(kpiTrend.cancel_rate.total)}</b> das vendas · ant. {fmtBp(kpiTrend.cancel_rate.previous_total)}</>} />
                </Box>

                {/* dash-grid — Métodos de pagamento / Status das vendas. */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' },
                    gap: 2,
                    mb: 2,
                    '& > *': { minWidth: 0 },
                }}>
                    {/* Métodos de Pagamento */}
                    <Card>
                        <CardContent sx={{ p: 2.25 }}>
                            <CardHeadRow
                                icon={<CreditCard size={18} strokeWidth={2} />}
                                iconBg="warning.bg"
                                iconFg="warning.main"
                                title="Métodos de Pagamento"
                                subtitle="Total recebido por forma · % do bruto"
                                loading={loading}
                            />
                            <PaymentRow
                                icon={<CreditCard size={15} strokeWidth={2} />}
                                iconBg="secondary.bg"
                                iconFg="secondary.main"
                                label="Crédito"
                                value={<AnimatedMoney value={resumeValues.total_amount_credit_card} />}
                                barWidth={payBar(resumeValues.total_amount_credit_card)}
                                pctLabel={fmtPct(parseMoney(resumeValues.total_amount_credit_card), grossValue)}
                                barColor="secondary.main"
                                loading={loading}
                            />
                            <PaymentRow
                                icon={<CreditCard size={15} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="primary.main"
                                label="Débito"
                                value={<AnimatedMoney value={resumeValues.total_amount_debit_card} />}
                                barWidth={payBar(resumeValues.total_amount_debit_card)}
                                pctLabel={fmtPct(parseMoney(resumeValues.total_amount_debit_card), grossValue)}
                                barColor="primary.main"
                                loading={loading}
                            />
                            <PaymentRow
                                icon={<QrCode size={15} strokeWidth={2} />}
                                iconBg="info.bg"
                                iconFg="brand2.main"
                                label="Pix"
                                value={<AnimatedMoney value={resumeValues.total_amount_pix} />}
                                barWidth={payBar(resumeValues.total_amount_pix)}
                                pctLabel={fmtPct(parseMoney(resumeValues.total_amount_pix), grossValue)}
                                barColor="brand2.main"
                                loading={loading}
                            />
                            <PaymentRow
                                icon={<Banknote size={15} strokeWidth={2} />}
                                iconBg="success.bg"
                                iconFg="success.main"
                                label="Dinheiro"
                                value={<AnimatedMoney value={resumeValues.total_amount_cash} />}
                                barWidth={payBar(resumeValues.total_amount_cash)}
                                pctLabel={fmtPct(parseMoney(resumeValues.total_amount_cash), grossValue)}
                                barColor="success.main"
                                loading={loading}
                            />
                        </CardContent>
                    </Card>

                    {/* Status das Vendas */}
                    <Card>
                        <CardContent sx={{ p: 2.25, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <CardHeadRow
                                icon={<Clock size={18} strokeWidth={2} />}
                                iconBg="success.bg"
                                iconFg="success.main"
                                title="Status das Vendas"
                                subtitle={loading ? '' : `${(resumeValues.count_sales || 0).toLocaleString('pt-BR')} vendas no período`}
                                loading={loading}
                            />

                            {loading ? (
                                <>
                                    {[1, 2, 3].map((item) => (
                                        <PaymentRow key={item} loading />
                                    ))}
                                </>
                            ) : hasStatusData ? (
                                <>
                                    {resumeValues.chart_sale_status.map((item, index) => {
                                        const tone = getStatusTone(item.label);
                                        return (
                                            <PaymentRow
                                                key={`${item.label}-${index}`}
                                                icon={getStatusIcon(item.label)}
                                                iconBg={tone.bg}
                                                iconFg={tone.fg}
                                                label={item.label}
                                                value={<AnimatedNumber value={item.value || 0} />}
                                                barWidth={statusBar(item.value)}
                                                pctLabel={fmtPct(item.value, statusTotal)}
                                                barColor={tone.fg}
                                            />
                                        );
                                    })}
                                    {/* Total de vendas (antes no centro do donut). */}
                                    <Box sx={{ mt: 'auto', pt: '11px', borderTop: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>Total de vendas</Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary' }}>
                                            <AnimatedNumber value={resumeValues.count_sales || 0} />
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <EmptyMiniState
                                    icon={<BarChart3 size={24} strokeWidth={2} />}
                                    title="Nenhum dado disponível"
                                    subtitle="Selecione um período para visualizar os dados"
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Vendas por Dia — panel-head (sem ícone, igual ao protótipo) + BarChart. */}
                <Card>
                    <CardContent sx={{ p: 2.25 }}>
                        <Box sx={{ mb: 2 }}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={160} height={22} />
                                    <Skeleton variant="text" width={220} height={16} />
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                        Vendas por Dia
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                        Total de vendas por dia · dias com movimento no período selecionado
                                    </Typography>
                                </>
                            )}
                        </Box>

                        <Box sx={{ height: 240, width: '100%' }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: '100%', px: 1 }}>
                                    {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                                        <Skeleton key={item} variant="rectangular" sx={{ flex: 1, borderRadius: '4px 4px 0 0' }} height={80 + (item % 4) * 30} />
                                    ))}
                                </Box>
                            ) : hasBarData ? (
                                <BarChart
                                    height={240}
                                    margin={{ top: 28, right: 8, bottom: 24, left: 8 }}
                                    borderRadius={6}
                                    dataset={totalSaleByDay}
                                    categoryGapRatio={0.6}
                                    hideLegend
                                    xAxis={[{
                                        scaleType: 'band',
                                        dataKey: 'day',
                                        disableLine: true,
                                        disableTicks: true,
                                        tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                                    }]}
                                    yAxis={[{ disableLine: true, disableTicks: true }]}
                                    series={[{
                                        dataKey: 'total',
                                        label: 'Total de Vendas',
                                        valueFormatter: (value) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                        color: 'url(#saleDashboardBarGradient)',
                                        // `barLabelPlacement` só é respeitado quando definido por série
                                        // (não como prop do <BarChart>, apesar do que sugere o `.d.ts`
                                        // do @mui/x-charts@8.10.2 — confirmado lendo BarPlot.js/
                                        // useBarPlotData.js: só `barLabel` tem fallback no nível do gráfico).
                                        barLabel: (item) => formatCompactCurrency(item.value),
                                        barLabelPlacement: 'outside',
                                    }]}
                                    sx={{
                                        // Sem eixo Y (protótipo `.bar-chart` não tem escala — só rótulo
                                        // de valor sobre cada barra e o dia embaixo, DESIGN-SYSTEM.md §5.9).
                                        '& .MuiChartsAxis-directionY': { display: 'none' },
                                        '& .MuiBarLabel-root': { fontSize: 10, fontWeight: 700, fill: theme.palette.text.secondary },
                                    }}
                                >
                                    <defs>
                                        <linearGradient id="saleDashboardBarGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.brand2.main} />
                                            <stop offset="100%" stopColor={theme.palette.primary.main} />
                                        </linearGradient>
                                    </defs>
                                </BarChart>
                            ) : (
                                <EmptyMiniState
                                    icon={<BarChart3 size={24} strokeWidth={2} />}
                                    title="Nenhuma venda no período"
                                    subtitle="Ajuste o período acima para visualizar a evolução diária"
                                />
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <DialogForm
                open={openMessageDialog}
                onClose={() => setOpenMessageDialog(false)}
                onSubmit={() => setOpenMessageDialog(false)}
                title={'Atenção'}
                iconType="warning"
                content={messageDialog}
                cancelButtonText="Fechar"
                hideConfirmButton
            />
        </PageScaffold>
    )
}
